import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DashboardAPI, FeedbackAPI, resolveAsset, api } from "../../services/api";
import { ReactComponent as CheckIcon } from "../../assets/icons/feedback1/check.svg";
import { ReactComponent as UpIcon } from "../../assets/icons/feedback1/trendingup.svg";
import { ReactComponent as DownIcon } from "../../assets/icons/feedback1/trendingdown.svg";
import { ReactComponent as MagnifyingGlassIcon } from "../../assets/icons/feedback1/magnifyingglass.svg";
import { ReactComponent as PointIcon } from "../../assets/icons/feedback1/point.svg";

// ---------- Helpers ----------
const deltaClass = (tone) =>
  tone === "up" ? "text-emerald-500" : "text-rose-500";

const catPill = (cat) => {
  if (!cat) return "bg-slate-100 text-slate-600";

  const lower = cat.toLowerCase().replace(/\s/g, "");

  if (lower === "ทางปิด" || lower === "closedroute") {
    return "bg-orange-100 text-orange-700";
  }

  if (lower === "ห้องย้ายที่" || lower === "roommoved") {
    return "bg-yellow-100 text-yellow-700";
  }

  if (lower === "ตำแหน่งไม่ตรง" || lower === "locationmismatch") {
    return "bg-blue-100 text-blue-700";
  }

  if (
    lower === "อื่นๆ" ||
    lower === "อื่นฯ" ||
    lower === "other" ||
    lower === "others"
  ) {
    return "bg-purple-100 text-purple-700";
  }

  return "bg-slate-100 text-slate-600";
};

function parseMaybeJson(value) {
  if (!value || typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getFeedbackClosure(feedback) {
  const closure = parseMaybeJson(feedback?.closure);
  const reportedClosure = parseMaybeJson(feedback?.reportedClosure);
  const closureReview = parseMaybeJson(feedback?.closureReview);
  const reported = closure || reportedClosure || closureReview;

  if (reported) return reported;

  const floorId = feedback?.closureFloorId ?? feedback?.closure_floor_id;
  const x = feedback?.closureX ?? feedback?.closure_x;
  const y = feedback?.closureY ?? feedback?.closure_y;
  if (floorId == null || x == null || y == null) return null;
  return {
    floorId,
    x: Number(x),
    y: Number(y),
    radiusPx: Number(feedback?.closureRadiusPx ?? feedback?.closure_radius_px ?? 80),
    status: feedback?.closureStatus || "pending",
  };
}

const getClosureFlowState = (row) => {
  const closure = getFeedbackClosure(row);
  if (!closure) return null;
  const status = String(closure.status || "pending").toLowerCase();
  if (["pending", "reported", "review", "reviewing"].includes(status)) return "review";
  if (status === "active") return "active";
  if (status === "resolved") return "resolved";
  if (["rejected", "cancelled", "canceled"].includes(status)) return "rejected";
  return null;
};

const statusPill = (status, closureState) => {
  if (closureState === "review") return "bg-amber-50 text-amber-700";
  if (closureState === "active") return "bg-red-50 text-red-600";
  if (closureState === "rejected") return "bg-slate-100 text-slate-500";
  if (status === "pending") return "bg-rose-50 text-rose-600";
  if (status === "done" || status === "resolved") return "bg-emerald-50 text-emerald-600";
  if (status === "rejected") return "bg-slate-100 text-slate-500";
  return "bg-slate-100 text-slate-600";
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeChartPoint = (item, idx) => ({
  day: item.day || item.label || item.date || item.createdAt || String(idx + 1),
  a: toNumber(item.a ?? item.done ?? item.resolved ?? item.totalDone ?? item.total ?? item.count),
  b: toNumber(item.b ?? item.pending ?? item.unresolved ?? item.totalPending),
});

export default function FeedbackPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [period, setPeriod] = useState("weekly"); // weekly | monthly
  const [tab, setTab] = useState("all"); // all | done | pending
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [serverRows, setServerRows] = useState(null);
  const [serverKpi, setServerKpi] = useState(null);
  const [serverWeekly, setServerWeekly] = useState(null);

  const kpi = [
    {
      label: t("admin.feedback.totalFeedback"),
      value: "1,284",
      delta: "+12.5%",
      deltaTone: "up",
    },
    {
      label: t("admin.feedback.resolvedToday"),
      value: "15",
      delta: "+8.4%",
      deltaTone: "up",
    },
    {
      label: t("admin.feedback.pendingFeedback"),
      value: "42",
      delta: "-5.2%",
      deltaTone: "down",
    },
  ];

  const weeklyBars = [
    { day: "Mon", a: 38, b: 22 },
    { day: "Tue", a: 58, b: 18 },
    { day: "Wed", a: 22, b: 28 },
    { day: "Thu", a: 44, b: 16 },
    { day: "Fri", a: 74, b: 10 },
    { day: "Sat", a: 62, b: 12 },
    { day: "Sun", a: 54, b: 16 },
  ];

  const monthlyBars = [
    { day: "W1", a: 176, b: 82 },
    { day: "W2", a: 214, b: 73 },
    { day: "W3", a: 188, b: 91 },
    { day: "W4", a: 246, b: 68 },
    { day: "W5", a: 92, b: 31 },
  ];

  const mockRows = [
    {
      id: 1,
      img: "https://images.unsplash.com/photo-1529421308261-680c3a1b70c7?auto=format&fit=crop&w=200&q=60",
      text: "รพ.มหาราชนครเชียงใหม่ ปรับปรุงระบบลิฟต์ใหม่",
      cat: "ทางเข้า",
      catTone: "orange",
      date: "24 มค 2566",
      status: "pending",
    },
    {
      id: 2,
      img: "https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?auto=format&fit=crop&w=200&q=60",
      text: "เหม็นกับอาคารใหญ่ ๆ อย่างโรงพยาบาลสร้างทางเดินใหม่",
      cat: "ห้องน้ำ",
      catTone: "amber",
      date: "24 มค 2566",
      status: "pending",
    },
    {
      id: 3,
      img: "https://images.unsplash.com/photo-1529429611273-0b1b2f6d4c64?auto=format&fit=crop&w=200&q=60",
      text: "โดยรวมใช้งานดี ไม่ต้องเปิด GPS ก็ยังนำทางได้",
      cat: "ตำแหน่งไม่ตรง",
      catTone: "blue",
      date: "24 มค 2566",
      status: "pending",
    },
    {
      id: 4,
      img: "https://images.unsplash.com/photo-1526779259212-939e64788e3c?auto=format&fit=crop&w=200&q=60",
      text: "ชอบที่ระบบบอกขั้นตอนการเดินเป็นช่วง ๆ เดินเสร็จ",
      cat: "อื่นๆ",
      catTone: "purple",
      date: "24 มค 2566",
      status: "pending",
    },
    {
      id: 5,
      img: "https://images.unsplash.com/photo-1529421308261-680c3a1b70c7?auto=format&fit=crop&w=200&q=60",
      text: "แผนที่ดูเข้าใจง่าย คล้าย Google Maps ทำให้ใช้ง่าย",
      cat: "อื่นๆ",
      catTone: "purple",
      date: "24 มค 2566",
      status: "done",
    },
    {
      id: 6,
      img: "https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?auto=format&fit=crop&w=200&q=60",
      text: "ชอบที่ระบบบอกขั้นตอนการเดินเป็นช่วง ๆ เดินเสร็จ",
      cat: "อื่นๆ",
      catTone: "purple",
      date: "24 มค 2566",
      status: "done",
    },
  ];

  const normalizeChartData = (data, period) => {
    const list = Array.isArray(data) ? data.map(normalizeChartPoint) : [];
    if (!list.length) return period === "monthly" ? monthlyBars : weeklyBars;

    if (period !== "monthly") return list.slice(-7);

    const groups = [];
    list.forEach((item, idx) => {
      const groupIdx = Math.floor(idx / 7);
      if (!groups[groupIdx]) {
        groups[groupIdx] = { day: `W${groupIdx + 1}`, a: 0, b: 0 };
      }
      groups[groupIdx].a += item.a;
      groups[groupIdx].b += item.b;
    });

    return groups.filter(Boolean);
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setPage(1);
  };

  const handleSearchChange = (newQ) => {
    setQ(newQ);
    setPage(1);
  };

  useEffect(() => {
    const params = { page, limit: 10 };
    if (tab !== "all") params.status = tab;
    if (q.trim()) params.q = q.trim();
    FeedbackAPI.list(params)
      .then((r) => {
        setServerRows(r?.feedbacks || r?.items || (Array.isArray(r) ? r : []));
        setTotalPages(r?.totalPages || 1);
      })
      .catch(() => {
        setServerRows(null);
        setTotalPages(1);
      });
  }, [tab, q, page]);

  useEffect(() => {
    const updated = location.state?.updatedFeedback;
    if (!updated) return;

    setServerRows((prev) => {
      if (!Array.isArray(prev)) return prev;
      const nextStatus = updated.status;
      const shouldShow =
        tab === "all" ||
        (tab === "pending" && nextStatus === "pending") ||
        (tab === "done" && nextStatus === "done");

      const exists = prev.some((row) => String(row.id) === String(updated.id));
      if (!shouldShow) {
        return prev.filter((row) => String(row.id) !== String(updated.id));
      }
      if (exists) {
        return prev.map((row) =>
          String(row.id) === String(updated.id) ? { ...row, ...updated } : row
        );
      }
      return [updated, ...prev];
    });
  }, [location.state, tab]);

  useEffect(() => {
    DashboardAPI.feedbackStats()
      .then((s) => {
        setServerKpi(s);
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    let active = true;
    setServerWeekly(null);

    api.getFeedbackChart(period)
      .then((data) => {
        if (active) setServerWeekly(data);
      })
      .catch(() => {
        if (active) setServerWeekly(null);
      });

    return () => {
      active = false;
    };
  }, [period]);

  const kpiCards = serverKpi
    ? [
      {
        label: kpi[0].label,
        value: String(serverKpi.total ?? 0),
        delta: serverKpi.totalDelta || "",
        deltaTone: serverKpi.totalDeltaTone || "up",
      },
      { label: kpi[1].label, value: String(serverKpi.doneToday ?? 0), delta: "", deltaTone: "up" },
      { label: kpi[2].label, value: String(serverKpi.pending ?? 0), delta: "", deltaTone: "down" },
    ]
    : kpi;

  const bars = useMemo(
    () => normalizeChartData(serverWeekly, period),
    [serverWeekly, period]
  );

  const rows = useMemo(() => {
    if (serverRows) return serverRows;
    const s = q.trim().toLowerCase();
    return mockRows.filter((r) => {
      const matchText = !s || r.text.toLowerCase().includes(s);
      const matchTab =
        tab === "all"
          ? true
          : tab === "done"
            ? r.status === "done"
            : r.status === "pending";
      return matchText && matchTab;
    });
  }, [q, tab, serverRows]);

  const getLocalizedCategory = (cat) => {
    if (!cat) return "";
    const lower = cat.toLowerCase();
    if (lower === "ตำแหน่งไม่ตรง" || lower === "locationmismatch") return t("user.feedback.problems.locationMismatch");
    if (lower === "ห้องย้ายที่" || lower === "roommoved") return t("user.feedback.problems.roomMoved");
    if (lower === "ทางปิด" || lower === "closedroute") return t("user.feedback.problems.closedRoute");
    if (lower === "อื่นๆ" || lower === "อื่น ๆ" || lower === "other" || lower === "others") return t("user.feedback.problems.other");
    if (lower === "ห้องน้ำ" || lower === "bathroom") return t("user.layout.restroom");
    if (lower === "ทางเข้า" || lower === "entrance") return t("admin.navigation.drawingLine");
    return cat;
  };

  return (
    <div className="font-sans">
      {/* Title */}
      <div className="mb-4">
        <div className="flex items-center gap-1 text-slate-900">
          <button className="p-1 text-3xl font-base rounded-lg hover:bg-slate-100">‹</button>
          <h1 className="text-xl font-semibold">
            {t("admin.feedback.userFeedbackTitle")}
          </h1>
        </div>
        <p className="p-1 text-sm text-slate-700">
          {t("admin.feedback.userFeedbackSub")}
        </p>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {kpiCards.map((x) => {
          const Icon = x.deltaTone === "up" ? UpIcon : DownIcon;

          return (
            <div
              key={x.label}
              className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm"
            >
              <div className="text-sm font-semibold text-slate-500">{x.label}</div>

              <div className="mt-2 flex items-end justify-between">
                <div className="text-3xl font-bold text-slate-900">
                  {x.value}
                </div>

                <div
                  className={`text-sm font-medium px-2.5 py-1 rounded-full ${deltaClass(
                    x.deltaTone,
                  )}`}
                >
                  <span className="flex items-center gap-1">
                    {x.delta}
                    <Icon className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Chart Card */}
      <section className="mt-4 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="font-semibold text-slate-900">
            {period === "monthly"
              ? t("admin.feedback.monthlyFeedback")
              : t("admin.feedback.weeklyFeedback")}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPeriod("weekly")}
              className={
                "px-4 py-1.5 rounded-2xl text-xs font-bold border " +
                (period === "weekly"
                  ? "bg-[#005EA4] text-white border-[#005EA4]"
                  : "bg-white text-slate-700 border-white hover:bg-slate-50")
              }
            >
              WEEKLY
            </button>
            <button
              onClick={() => setPeriod("monthly")}
              className={
                "px-4 py-1.5 rounded-2xl text-xs font-bold border " +
                (period === "monthly"
                  ? "bg-[#005EA4] text-white border-[#005EA4]"
                  : "bg-white text-slate-700 border-white hover:bg-slate-50")
              }
            >
              MONTHLY
            </button>
          </div>
        </div>

        {/* Simple bars */}
        <div className="px-6 py-6">
          <div className="h-[220px] w-full rounded-lg bg-slate-50 border border-slate-100 flex items-end justify-between gap-3 px-4 py-6 overflow-x-auto">
            {bars.map((b) => {
              const maxVal = Math.max(...bars.map(x => Math.max(x.a || 0, x.b || 0)), 10);
              const h1 = Math.round(((b.a || 0) / maxVal) * 100);
              const h2 = Math.round(((b.b || 0) / maxVal) * 100);
              return (
                <div
                  key={b.day}
                  className="flex min-w-[56px] flex-1 flex-col items-center gap-3"
                >
                  <div className="w-full flex items-end justify-center gap-2 h-[160px]">
                    <div
                      className="w-5 sm:w-8 rounded-md bg-[#005EA4]"
                      style={{ height: `${h1}%` }}
                      title={`${t("admin.feedback.all")}: ${b.a}`}
                    />
                    <div
                      className="w-5 sm:w-8 rounded-md bg-blue-100"
                      style={{ height: `${h2}%` }}
                      title={`${t("admin.feedback.pendingFeedback")}: ${b.b}`}
                    />
                  </div>
                  <div className="text-xs font-semibold text-slate-500">
                    {b.day}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Table Card */}
      <section className="mt-4 bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="px-5 py-4 border-slate-100 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          {/* Left: table heading */}
          <div className="font-bold text-slate-900 m-2">{t("admin.feedback.latestFeedback")}</div>

          {/* Center: status filter tabs */}
          <div className="justify-self-center m-2">
            <div className="rounded-xl p-1 flex items-center">
              {[
                { k: "all", label: t("admin.feedback.all") },
                { k: "done", label: t("admin.feedbackDetail.resolved") },
                { k: "pending", label: t("admin.feedbackDetail.unresolved") },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => handleTabChange(t.k)}
                  className={
                    "px-4 py-2 rounded-2xl text-xs font-semibold " +
                    (tab === t.k
                      ? "bg-[#005EA4] text-white"
                      : "text-slate-500 hover:text-slate-700")
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right: search input */}
          <div className="justify-self-end m-2">
            <div className="hidden md:flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-3 min-w-[280px]">
              <MagnifyingGlassIcon className="w-4 h-4 text-slate-500" />
              <input
                className="w-full outline-none text-sm font-base"
                placeholder={t("common.search")}
                value={q}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="px-5 pb-4">
          {/* Mobile search */}
          <div className="md:hidden mt-3 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-3">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-500" />
            <input
              className="w-full outline-none text-sm font-semibold"
              placeholder={t("common.search")}
              value={q}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          {/* Table */}
          <div className="m-2 border border-slate-200 rounded-xl overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="text-slate-500 bg-slate-50 border-b text-sm font-thin">
                    <th className="text-center py-4 px-2">{t("admin.feedback.imageCol")}</th>
                    <th className="text-left py-4 px-2 w-[280px]">
                      {t("admin.feedback.contentCol")}
                    </th>
                    <th className="text-center py-4 px-2 whitespace-nowrap">
                      {t("admin.feedback.categoryCol")}
                    </th>
                    <th className="text-center py-4 px-2">{t("admin.feedback.dateCol")}</th>
                    <th className="text-center py-4 px-2">{t("admin.feedback.statusCol")}</th>
                    <th className="text-center py-4 px-2"></th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => {
                    const text = r.text || r.description || "";
                    const cat = r.cat || r.category || "";
                    const img = resolveAsset(r.img || r.imageUrl);
                    const closureState = getClosureFlowState(r);
                    const feedbackStatus = String(r.status || "pending").toLowerCase();
                    const statusLabel =
                      closureState === "review"
                        ? "รอตรวจสอบทางปิด"
                        : closureState === "active"
                          ? "ยืนยันทางปิดแล้ว"
                          : closureState === "rejected" || feedbackStatus === "rejected"
                            ? "ปฏิเสธแล้ว"
                            : feedbackStatus === "pending"
                              ? t("admin.feedbackDetail.unresolved")
                              : t("admin.feedbackDetail.resolved");
                    const actionLabel =
                      closureState === "review"
                        ? "ตรวจสอบทางปิด"
                        : closureState === "active"
                          ? "ติดตามการแก้ไข"
                          : t("admin.feedback.viewDetails");
                    const canOpenDetail =
                      feedbackStatus === "pending" ||
                      closureState === "review" ||
                      closureState === "active";
                    const date =
                      r.date ||
                      (r.createdAt
                        ? new Date(r.createdAt).toLocaleDateString(i18n.language === "th" ? "th-TH" : "en-US")
                        : "");
                    return (
                      <tr key={r.id} className="text-slate-700">
                        <td className="py-3 px-2">
                          <div className="flex justify-center">
                            {img ? (
                              <img
                                src={img}
                                alt=""
                                className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200" />
                            )}
                          </div>
                        </td>

                        <td className="py-4 pr-1 pl-2 text-left w-[280px]">
                          <div className="font-semibold text-slate-900 line-clamp-1">
                            {text}
                          </div>
                        </td>

                        <td className="py-4 px-2 text-center">
                          <span
                            className={`mx-auto inline-flex items-center justify-center text-center text-xs font-bold w-[120px] px-2 py-1.5 rounded-full ${catPill(cat)}`}
                          >
                            {getLocalizedCategory(cat)}
                          </span>
                        </td>

                        <td className="py-4 px-2 text-center text-slate-500 font-semibold">
                          {date}
                        </td>

                        <td className="py-4 px-2 text-center">
                          <span
                            className={`inline-flex text-xs font-semibold px-3 py-1.5 rounded-full ${statusPill(
                              feedbackStatus,
                              closureState,
                            )}`}
                          >
                            <PointIcon className="w-1 h-1 text-center mt-1.5 mr-2 shrink-0" />
                            {statusLabel}
                          </span>
                        </td>

                        <td className="py-4 px-2">
                          <div className="flex justify-center">
                            {canOpenDetail ? (
                              <button
                                onClick={() =>
                                  navigate(`/admin/feedback/${r.id}`, {
                                    state: { feedback: r },
                                  })
                                }
                                className="text-blue-500 font-semibold hover:underline"
                              >
                                {actionLabel}
                              </button>
                            ) : (
                              <CheckIcon className="w-6 h-6 text-green-500" />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="w-9 h-9 rounded-xl hover:bg-slate-50 font-black text-slate-700 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                const isCurrent = p === page;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={
                      "rounded-xl px-2 py-1.5 text-sm font-semibold border " +
                      (isCurrent
                        ? "border-blue-600 bg-blue-600 text-white font-semibold"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
                    }
                  >
                    {String(p).padStart(2, "0")}
                  </button>
                );
              })}
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="w-9 h-9 rounded-xl hover:bg-slate-50 font-black text-slate-700 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                ›
              </button>
            </div>
          )}
        </div>
      </section>
      {/* Footer */}
      <div className="mt-3 text-left border border-slate-200 rounded-lg p-4 bg-white text-xs text-slate-500">
        {t("admin.feedbackDetail.copyright")}
      </div>
    </div>
  );
}
